import { useCallback, useEffect, useState } from "react";
import {
  connectLinear,
  disconnectLinear,
  getLinearInstallation,
  refreshLinearTeams,
  LinearApiError,
  type LinearInstallationView,
} from "../lib/api";

/**
 * Linear integration panel — shown on the Account page, right under the
 * GitHub panel. Mirrors GitHubIntegrationPanel's structure but the auth
 * model is dramatically simpler: paste a Linear personal API key, we
 * validate it against `viewer + organization + teams`, store the key
 * + workspace metadata, and you're connected.
 *
 * Two states:
 *   - disconnected (no row in linear_installations) → form
 *   - connected   (row exists)                     → "Connected to ACME"
 *                                                     + default-team picker
 *                                                     + Disconnect button
 *
 * Errors:
 *   - 402 INTEGRATION_NOT_ALLOWED → upgrade-to-Pro nudge
 *   - 401 LINEAR_AUTH_FAILED       → "key was rejected, double-check"
 *   - other → raw error message
 */
export function LinearIntegrationPanel(): JSX.Element {
  const [installation, setInstallation] = useState<LinearInstallationView | null>(
    null,
  );
  const [loaded, setLoaded] = useState(false);
  const [apiKey, setApiKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [needsUpgrade, setNeedsUpgrade] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const inst = await getLinearInstallation();
      setInstallation(inst);
    } catch (err) {
      // If the user isn't signed in we'll just show the form-style call
      // to action — no need to noisy-error in that case.
      if (err instanceof LinearApiError && err.status === 401) {
        setInstallation(null);
      } else {
        setError(err instanceof Error ? err.message : String(err));
      }
    } finally {
      setLoaded(true);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  function handleApiError(err: unknown): void {
    if (err instanceof LinearApiError) {
      if (err.code === "INTEGRATION_NOT_ALLOWED") {
        setNeedsUpgrade(true);
        setError(err.message);
        return;
      }
      setError(err.message);
      return;
    }
    setError(err instanceof Error ? err.message : String(err));
  }

  async function handleConnect(e: React.FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setError(null);
    setInfo(null);
    setNeedsUpgrade(false);
    if (!apiKey.trim()) {
      setError("API key required");
      return;
    }
    setBusy(true);
    try {
      const inst = await connectLinear(apiKey.trim());
      setInstallation(inst);
      setApiKey(""); // clear from React state — never echo back
      setInfo(`Connected to ${inst.workspaceName}.`);
    } catch (err) {
      handleApiError(err);
    } finally {
      setBusy(false);
    }
  }

  async function handleDisconnect(): Promise<void> {
    if (!window.confirm("Disconnect Linear from UAIP? Issue creation will stop."))
      return;
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      await disconnectLinear();
      setInstallation(null);
      setInfo("Linear disconnected.");
    } catch (err) {
      handleApiError(err);
    } finally {
      setBusy(false);
    }
  }

  async function handleRefreshTeams(): Promise<void> {
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      const inst = await refreshLinearTeams();
      setInstallation(inst);
      setInfo("Refreshed team list.");
    } catch (err) {
      handleApiError(err);
    } finally {
      setBusy(false);
    }
  }

  return (
    <section
      id="linear"
      className="rounded-xl border border-slate-700 bg-slate-900/60 p-5"
    >
      <header className="mb-3 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold">Linear</h2>
          <p className="text-xs text-slate-400">
            File heal &amp; triage proposals as Linear issues. Pro plan and up.
          </p>
        </div>
        {installation ? (
          <span className="rounded-full bg-emerald-900/40 px-3 py-1 text-xs font-medium text-emerald-300">
            Connected
          </span>
        ) : (
          <span className="rounded-full bg-slate-800 px-3 py-1 text-xs font-medium text-slate-400">
            Not connected
          </span>
        )}
      </header>

      {error && (
        <div className="mb-3 rounded-md border border-rose-700/60 bg-rose-900/30 px-3 py-2 text-sm text-rose-200">
          {error}
          {needsUpgrade && (
            <>
              {" "}
              <a className="underline" href="/account#plan">
                Upgrade to Pro
              </a>
              .
            </>
          )}
        </div>
      )}
      {info && !error && (
        <div className="mb-3 rounded-md border border-emerald-700/60 bg-emerald-900/30 px-3 py-2 text-sm text-emerald-200">
          {info}
        </div>
      )}

      {!loaded ? (
        <p className="text-sm text-slate-400">Loading…</p>
      ) : installation ? (
        <ConnectedView
          installation={installation}
          onDisconnect={handleDisconnect}
          onRefreshTeams={handleRefreshTeams}
          busy={busy}
        />
      ) : (
        <form onSubmit={handleConnect} className="flex flex-col gap-2">
          <label className="text-xs font-medium text-slate-300">
            Linear personal API key
          </label>
          <input
            type="password"
            autoComplete="off"
            spellCheck={false}
            placeholder="lin_api_…"
            value={apiKey}
            onChange={(e) => setApiKey(e.target.value)}
            className="rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 outline-none focus:border-indigo-500"
          />
          <p className="text-xs text-slate-500">
            Create one at{" "}
            <a
              href="https://linear.app/settings/api"
              target="_blank"
              rel="noreferrer noopener"
              className="text-indigo-300 underline"
            >
              linear.app/settings/api
            </a>
            . The key is stored encrypted on the server and never echoed back.
          </p>
          <div className="mt-1">
            <button
              type="submit"
              disabled={busy || !apiKey.trim()}
              className="rounded-md bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-indigo-500 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {busy ? "Connecting…" : "Connect Linear"}
            </button>
          </div>
        </form>
      )}
    </section>
  );
}

function ConnectedView(props: {
  installation: LinearInstallationView;
  onDisconnect: () => void;
  onRefreshTeams: () => void;
  busy: boolean;
}): JSX.Element {
  const { installation: inst, onDisconnect, onRefreshTeams, busy } = props;
  return (
    <div className="flex flex-col gap-3 text-sm">
      <div className="grid grid-cols-2 gap-3 text-slate-300">
        <Field label="Workspace" value={inst.workspaceName} />
        <Field
          label="URL"
          value={
            inst.workspaceUrlKey ? `linear.app/${inst.workspaceUrlKey}` : "—"
          }
        />
        <Field
          label="Default team"
          value={
            inst.defaultTeamName
              ? `${inst.defaultTeamName} (${inst.defaultTeamKey ?? "?"})`
              : "(none — pick one per issue)"
          }
        />
        <Field
          label="Connected"
          value={new Date(inst.createdAt).toLocaleString()}
        />
      </div>

      {inst.teams && inst.teams.length > 0 && (
        <div>
          <p className="mb-1 text-xs font-medium text-slate-400">
            Teams visible to this key ({inst.teams.length})
          </p>
          <ul className="flex flex-wrap gap-2">
            {inst.teams.map((t) => (
              <li
                key={t.id}
                className="rounded-md bg-slate-800 px-2 py-1 text-xs text-slate-300"
              >
                {t.name}{" "}
                <span className="text-slate-500">({t.key})</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="flex gap-2 pt-2">
        <button
          type="button"
          onClick={onRefreshTeams}
          disabled={busy}
          className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300 hover:bg-slate-800 disabled:opacity-60"
        >
          Refresh teams
        </button>
        <button
          type="button"
          onClick={onDisconnect}
          disabled={busy}
          className="rounded-md border border-rose-800 px-3 py-1.5 text-xs text-rose-300 hover:bg-rose-900/30 disabled:opacity-60"
        >
          Disconnect
        </button>
      </div>
    </div>
  );
}

function Field(props: { label: string; value: string }): JSX.Element {
  return (
    <div>
      <p className="text-xs font-medium uppercase tracking-wide text-slate-500">
        {props.label}
      </p>
      <p className="mt-0.5 text-slate-200">{props.value}</p>
    </div>
  );
}
